<template>
    <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
            <label for="profile-first-name" class="flex flex-col gap-2">
                <span class="body-small-semibold uppercase text-control-label">{{ t("components.profile.edit.firstName") }}</span>
                <input
                    id="profile-first-name"
                    ref="firstNameInput"
                    v-model="profileForm.first_name"
                    type="text"
                    autocomplete="given-name"
                    :maxlength="255"
                    required
                    :aria-invalid="firstNameError || undefined"
                    :aria-describedby="firstNameError ? 'profile-first-name-error' : undefined"
                    class="w-full rounded-xl border border-control-border bg-control-bg px-3 py-2 body text-control-text-strong focus-visible:border-control-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-control-border-strong"
                    :placeholder="hrisData?.first_name ?? t('components.profile.edit.firstNamePlaceholder')"
                    @input="firstNameError = false" />
            </label>
            <span v-if="firstNameError" id="profile-first-name-error" role="alert" class="body-small text-button-danger">{{ t("components.profile.edit.firstNameRequired") }}</span>
        </div>
        <label for="profile-last-name" class="flex flex-col gap-2">
            <span class="body-small-semibold uppercase text-control-label">{{ t("components.profile.edit.lastName") }}</span>
            <input
                id="profile-last-name"
                v-model="profileForm.last_name"
                type="text"
                autocomplete="family-name"
                :maxlength="255"
                class="w-full rounded-xl border border-control-border bg-control-bg px-3 py-2 body text-control-text-strong focus-visible:border-control-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-control-border-strong"
                :placeholder="hrisData?.last_name ?? t('components.profile.edit.lastNamePlaceholder')" />
        </label>
        <label for="profile-title" class="flex flex-col gap-2">
            <span class="body-small-semibold uppercase text-control-label">{{ t("components.profile.edit.jobTitle") }}</span>
            <input
                id="profile-title"
                v-model="profileForm.title"
                type="text"
                autocomplete="organization-title"
                :maxlength="255"
                class="w-full rounded-xl border border-control-border bg-control-bg px-3 py-2 body text-control-text-strong focus-visible:border-control-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-control-border-strong"
                :placeholder="hrisData?.job_title ?? t('components.profile.edit.jobTitlePlaceholder')" />
        </label>
        <label for="profile-division" class="flex flex-col gap-2">
            <span class="body-small-semibold uppercase text-control-label">{{ t("components.profile.edit.team") }}</span>
            <input
                id="profile-division"
                v-model="profileForm.division"
                type="text"
                :maxlength="255"
                class="w-full rounded-xl border border-control-border bg-control-bg px-3 py-2 body text-control-text-strong focus-visible:border-control-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-control-border-strong"
                :placeholder="hrisData?.department ?? t('components.profile.edit.teamPlaceholder')" />
        </label>
        <AccountSettingToggle id="profile-is-manager" v-model="profileForm.is_manager" :label="t('components.profile.edit.leadTeam')" :bold="false" :compact="true" />
        <div class="flex items-center justify-end gap-3">
            <span v-if="saveError" role="alert" class="body-small text-button-danger">{{ saveError }}</span>
            <BaseButton theme="tertiary" size="sm" :disabled="isBusy" @click="emit('cancelled')"> {{ t("common.cancel") }} </BaseButton>
            <BaseButton theme="secondary" size="sm" :disabled="isBusy" @click="handleSave">
                {{ isBusy ? t("components.profile.edit.saving") : t("common.save") }}
            </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { ProfileIdentityUpdate } from "~vue/api/generated/users";
import type { HrisData } from "~vue/appTypes";
import type { ProfileIdentity } from "~vue/types/profile";
import AccountSettingToggle from "~vue/UserProfile/AccountSettingToggle.vue";
import { computed, onMounted, reactive, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import BaseButton from "./BaseButton.vue";

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        identity: ProfileIdentity;
        hrisData?: HrisData | null;
        afterSave?: () => Promise<void>;
    }>(),
    {
        hrisData: null,
    },
);

const emit = defineEmits<{
    saved: [];
    cancelled: [];
}>();

const profileForm = reactive({ ...props.identity });

const saveError = ref<string | null>(null);
const firstNameError = ref(false);
const firstNameInput = useTemplateRef("firstNameInput");

onMounted(() => firstNameInput.value?.focus());

const identityPatch = ref<ProfileIdentityUpdate>({});
const { execute: executeSave, statusCode: saveStatus, isFetching: isSaving } = useFetch("/v1/users/identity", { immediate: false }).patch(identityPatch);
const isRefreshing = ref(false);
const isBusy = computed(() => isSaving.value || isRefreshing.value);

async function handleSave(): Promise<void> {
    saveError.value = null;
    // Only edited fields are sent, so untouched prefills are never saved as the user's edits.
    const patch: ProfileIdentityUpdate = {};
    for (const field of ["first_name", "last_name", "title", "division"] as const) {
        if (profileForm[field].trim() !== props.identity[field].trim()) {
            patch[field] = profileForm[field];
        }
    }
    if (profileForm.is_manager !== props.identity.is_manager) {
        patch.is_manager = profileForm.is_manager;
    }
    // First name is required only when the name is being changed, so a user
    // with no stored first name can still save unrelated field edits.
    const nameEdited = patch.first_name !== undefined || patch.last_name !== undefined;
    firstNameError.value = nameEdited && !profileForm.first_name.trim();
    if (firstNameError.value) {
        firstNameInput.value?.focus();
        return;
    }
    if (Object.keys(patch).length === 0) {
        emit("cancelled");
        return;
    }

    identityPatch.value = patch;
    await executeSave();

    // A cancelled request (e.g. no auth token) has no status, so only a 204 counts as saved.
    if (saveStatus.value === 204) {
        // Stay open until the page shows the saved values, so the editor can't be reopened with stale ones.
        isRefreshing.value = true;
        try {
            await props.afterSave?.();
        } finally {
            isRefreshing.value = false;
        }
        emit("saved");
    } else {
        saveError.value = t("components.profile.edit.saveFailed");
    }
}
</script>
